import React, { useCallback, useEffect, useMemo, useState, Children, cloneElement } from 'react';

// DEPENDENCIES
import lowerCase from 'lodash/lowerCase';
import uniqBy from 'lodash/uniqBy';
import format from 'date-fns/format';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

// GLOBAL VARIABLES
import { DATE_FORMATS } from 'globals.js';
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE, RECORD } from 'pathnames.js';

// GLOBAL FUNCTIONS
import { makePath, sleep, ensureBoolean } from 'functions.js';
import { recountActions } from 'actions.js';

// GLOBAL HELPERS
import { withRouter } from 'helpers/withRouter';
import usePrevious from 'helpers/usePrevious';
import useRecordView from 'helpers/getRecordView';
import useConfirmRevive from './useConfirmRevive';

// GLOBAL COMPONENTS
import Alert from 'core/tools/alert/Alert';
import Record from 'parts/record/Record';

// LOCAL FUNCTIONS
import { formatSaleAdd, formatSaleEdit, formatSaleView } from '../functions/formatSale';

// LOCAL COMPONENTS
import HeaderData from '../../../tools/HeaderData';
import SalesLogs from './SalesLogs';

// MAIN COMPONENT
const SalesDetail = compose(
  withRouter
)(({
  children,
  title,
  onEditClick,
  buttons,
  // REACT ROUTER
  location,
  navigate,
}) => {
  const { recordID } = useParams();

  // CONTEXT
  const { isAdd, isEdit, isView } = useRecordView();

  // STATE
  const [record, setRecord] = useState({});
  const [statusMessage, setStatusMessage] = useState();

  // REDUX
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);

  // MEMOS
  const {
    inventory,
    customer_first_name,
    customer_last_name,
    sales_status_name,
    is_for_wholesale,
    warranty_type,
    warranty_type_name
  } = useMemo(() => record || {}, [record]);
  const { stock_num, vehicle_year, make, model } = useMemo(
    () => inventory || {},
    [inventory]
  )
  const status = useMemo(
    () => lowerCase(sales_status_name).replace(/\s/g, ''),
    [sales_status_name]
  )
  const isBackout = useMemo(
    () => lowerCase(sales_status_name) === 'backout',
    [sales_status_name]
  )
  const isRevive = useMemo(
    () => !!location?.state?.revive,
    [location.state]
  )
  const endpoints = useMemo(
    () => isAdd ? {
      get: ENDPOINTS.inventory.single,
      update: ENDPOINTS.sales.single
    } : {
      single: ENDPOINTS.sales.single
    },
    [isAdd]
  )
  const initialValues = useMemo(
    () => ({
      date_created: format(new Date(), DATE_FORMATS.dateTime),
      daily_sales_inventory_id: parseInt(recordID),
      customer_type_id: 1,
      sale_type_category_id: is_for_wholesale ? 2 : 1,
      customer_is_employee: false,
      salesmanager1_site_user_id: user.role_key === "SLSSTF" ? user.site_user_id : '',
      aryOweItem: [],
      aryRebate: [],
      aryBankFee: [],
      aryOtherItem: [],
      aryDealerIncentive: [],
      aryDealerPack: [],
      arySpotInstruction: [],
      aryDownPayment: [],
      aryTrade:[],
      aryComment: [],
      documentAttachment: {},
      warranty_type,
      warranty_type_name
    }),
    [recordID, is_for_wholesale, user, warranty_type, warranty_type_name]
  )
  const addProps = useMemo(
    () => !isAdd ? {} : {
      successMessage: `Stock# ${record.stock_num} has been added to your ${record.new_used ? 'new' : 'pre-owned'} sale.`,
      successPathname: ({ sales_id }) => ({
        pathname: makePath(INTERFACE.amFinance, RECORD.view, sales_id),
        state: { isFromNewSale: true }
      }),
      backPathname: makePath(INTERFACE.inventory, RECORD.view, recordID)
    },
    [record, recordID, isAdd]
  )
  const successPathname = useMemo(() => {
    const { prevPath } = location?.state || {};
    return prevPath?.includes(RECORD.view) && prevPath?.split('/')[1];
  }, [location]);

  // HOOKS
  const handleConfirmRevive = useConfirmRevive({
    customerName: `${customer_first_name} ${customer_last_name}`,
    stockNum: stock_num
  });

  // CALLBACKS
  const formatRecord = useCallback(
    record => isAdd ? Object.assign(initialValues, formatSaleAdd(record)) : isEdit ? formatSaleEdit(record) : formatSaleView(record),
    [isAdd, isEdit, initialValues]
  )

  const formatParams = useCallback(
    v => ({
      ...v,
      // Per Usman, legacy removes any with duplicate names when saving
      arySpotInstruction: uniqBy((v.arySpotInstruction || []).map(a => ({ ...a, is_completed: ensureBoolean(a.is_completed) })), 'spot_instruction'),
      isRevive
    }),
    [isRevive]
  )
  const handleSuccess = useCallback(
    async d => {
      if (isRevive) handleConfirmRevive(d?.sales_id);
      await sleep(1000);
      dispatch(recountActions.toggle());
    },
    [dispatch, handleConfirmRevive, isRevive]
  )

  // EFFECTS
  const prevStatus = usePrevious(status);
  useEffect(() => {
    if (location?.state?.statusMessage) {
      setStatusMessage(location.state.statusMessage);

      // After setting status message, clear state
      navigate({ ...location, state: {} }, { replace: true });
    }
    // if status is updated
    else if (prevStatus && prevStatus !== status) {
      if (status === 'carryover') setStatusMessage('Sales has been carried over to current month.');
      else setStatusMessage(`Sale status has been changed to ${status}.`);
    }
  }, [prevStatus, status, navigate, location]);

  // RENDER
  return (
    <Record.Update
      title={title}
      recordIDKey="sales_id"
      recordLabel="sale"
      endpoints={endpoints}
      successPathname={successPathname}
      {...addProps}
      initialValues={initialValues}
      formatRecord={formatRecord}
      formatParams={formatParams}
      onLoadRecord={setRecord}
      onSuccess={handleSuccess}
      headerTools={[
        <HeaderData
          stockNo={stock_num}
          year={vehicle_year}
          make={make}
          model={model}
          disabled={isBackout}
          onEditClick={onEditClick}
        />
      ]}
      headerRight={isView ? <SalesLogs /> : null}
      buttonsBefore
      buttonsAfter
      buttons={buttons}
      saveButton={
        isRevive && {
          label: 'Revive'
        }
      }
      deleteButton={false}
      usePreloader={!isView}
      fixedHeader={isView}
    >
      <>
        {statusMessage ? (
          <Alert.Dismissable
            variant="primary"
            message={statusMessage}
            onClose={() => setStatusMessage(null)}
          />
        ) : null}
        {Children.map(children, (child, i) =>
          child
            ? cloneElement(child, {
                key: i,
                status
              })
            : null
        )}
      </>
    </Record.Update>
  );
});

// EXPORT
export default SalesDetail;
